import {
  type ChatMessage,
  type ChatMessagePart,
  ChatMessagePartType,
  type ComponentStreamEvent,
  type ControlStreamEvent,
  type FailedMessage,
  type FailedMessageStreamEvent,
  FailedMessageType,
  type SurveyStreamEvent,
  type ImageStreamEvent,
  MessageRole,
  type ModelMessage,
  type ModelMessageMetadataStreamEvent,
  type ProgressStreamEvent,
  type RawImageStreamEvent,
  type StreamDataChunk,
  type StreamEvent,
  StreamEventDataType,
  StreamEventName,
  ChatType,
  type TextStreamEvent,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';
import type {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {getUniqueString} from '@Utils/string.util';
import {omitKey} from '@Utils/record.util';

export type MessagesRecord = Record<string, ChatMessage>;

export interface StreamHandlers {
  onControlEvent: (event: ControlStreamEvent) => void;
  onProgressEvent: (event: ProgressStreamEvent) => void;
  onTextStreamEvent: (event: TextStreamEvent) => void;
  onImageStreamEvent: (event: ImageStreamEvent) => void;
  onRawImageStreamEvent: (event: RawImageStreamEvent) => void;
  onFailedMessageEvent: (event: FailedMessageStreamEvent) => void;
  onModelMessageMetadataEvent: (event: ModelMessageMetadataStreamEvent) => void;
  onSurveyEvent: (event: SurveyStreamEvent) => void;
  onComponentEvent: (event: ComponentStreamEvent) => void;
}

export const handleStreamEvent = (streamEvent: StreamDataChunk, handlers: StreamHandlers): void => {
  switch (streamEvent.event) {
    case StreamEventName.CONTROL:
      handlers.onControlEvent(streamEvent);
      break;
    case StreamEventName.THINKING:
      handlers.onProgressEvent(streamEvent);
      break;
    case StreamEventName.CHAT:
      handlers.onTextStreamEvent(streamEvent);
      break;
    case StreamEventName.IMAGE:
      if ('type' in streamEvent && streamEvent.type === StreamEventDataType.RAW) {
        handlers.onRawImageStreamEvent(streamEvent);
      } else {
        handlers.onImageStreamEvent(streamEvent);
      }
      break;
    case StreamEventName.FAILED_MESSAGE:
      handlers.onFailedMessageEvent(streamEvent);
      break;
    case StreamEventName.MODEL_MESSAGE_METADATA:
      handlers.onModelMessageMetadataEvent(streamEvent);
      break;
    case StreamEventName.SURVEY:
      handlers.onSurveyEvent(streamEvent);
      break;
    case StreamEventName.COMPONENT:
      handlers.onComponentEvent(streamEvent);
      break;
  }
};

export const deepCopyMessage = (message: ChatMessage): ChatMessage => {
  return {
    ...message,
    parts: message.parts?.map((part) => ({...part})),
  };
};

export const createThinkingMessage = (texts: unknown): ModelMessage => {
  // loadingTexts arrives from streamed SSE JSON, so it is not guaranteed to be a clean
  // string[] at runtime. Keep only real, non-empty strings: a non-string element would
  // otherwise reach the cycling loader and crash MarkdownRenderer on `.replace`. An empty
  // result renders as a plain loader (no cycling text) downstream.
  const safeTexts = Array.isArray(texts) ? texts.filter((text): text is string => typeof text === 'string' && text !== '') : [];

  return {
    role: MessageRole.ASSISTANT,
    isThinking: true,
    parts: [{type: ChatMessagePartType.TEXT, text: safeTexts[safeTexts.length - 1] ?? '', loadingTexts: safeTexts}],
    messageId: getUniqueString(),
  } as ModelMessage;
};

export const createStoppedErrorMessage = (): FailedMessage => {
  return {
    failureType: FailedMessageType.STOPPED,
    role: MessageRole.DEVELOPER,
    parts: [],
    messageId: getUniqueString(),
  };
};

export const filterOutPlaceholderImageParts = (parts: ChatMessagePart[]): ChatMessagePart[] => {
  return parts.filter(
    (part) =>
      !(
        part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE &&
        (('isRawBlurred' in part && part.isRawBlurred) || ('isSkeletonPlaceholder' in part && part.isSkeletonPlaceholder))
      )
  );
};

export const updateMessageWithTextStream = (existingMessage: ModelMessage, incomingMessage: StreamEvent): ModelMessage => {
  const updatedMessage = deepCopyMessage(existingMessage) as ModelMessage;
  const incomingParts = incomingMessage.message?.parts ?? [];
  const incomingTextPart = incomingParts.find((part) => part.type === ChatMessagePartType.TEXT);

  if (incomingTextPart) {
    const existingParts = updatedMessage.parts ?? [];
    let lastTextPartIndex = -1;

    for (let i = existingParts.length - 1; i >= 0; i--) {
      if (existingParts[i].type === ChatMessagePartType.TEXT) {
        lastTextPartIndex = i;
        break;
      }
    }

    if (lastTextPartIndex >= 0) {
      const textPart = existingParts[lastTextPartIndex] as {type: ChatMessagePartType.TEXT; text: string};
      textPart.text += incomingTextPart.text;
    } else {
      updatedMessage.parts = [...existingParts, {type: ChatMessagePartType.TEXT, text: incomingTextPart.text}];
    }
  }

  const nonTextParts = incomingParts.filter((part) => part.type !== ChatMessagePartType.TEXT);
  if (nonTextParts.length > 0) {
    updatedMessage.parts = [...(updatedMessage.parts ?? []), ...nonTextParts];
  }

  if (incomingMessage.message?.messageId) {
    updatedMessage.messageId = incomingMessage.message.messageId;
  }

  if (incomingMessage.message && 'usage' in incomingMessage.message && 'usage' in updatedMessage) {
    updatedMessage.usage = incomingMessage.message.usage;
  }

  return updatedMessage;
};

export const updateMessageWithImageStream = (existingMessage: ModelMessage, incomingMessage: StreamEvent): ModelMessage => {
  const updatedMessage = deepCopyMessage(existingMessage) as ModelMessage;
  const incomingParts = incomingMessage.message?.parts ?? [];
  const incomingImagePart = incomingParts.find((part) => part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE);
  const incomingTextPart = incomingParts.find((part) => part.type === ChatMessagePartType.TEXT);

  if (!incomingImagePart) {
    return updatedMessage;
  }

  if (incomingImagePart.type !== ChatMessagePartType.MODEL_GENERATED_IMAGE) {
    return updatedMessage;
  }

  incomingImagePart.isNewGeneration = true;
  const existingParts = updatedMessage.parts ?? [];
  const partsWithoutPlaceholders = filterOutPlaceholderImageParts(existingParts);
  const hasPlaceholder = partsWithoutPlaceholders.length < existingParts.length;
  const partsWithoutRawText = hasPlaceholder ? partsWithoutPlaceholders.filter((part) => part.type !== ChatMessagePartType.TEXT) : partsWithoutPlaceholders;
  updatedMessage.parts = incomingTextPart ? [...partsWithoutRawText, incomingTextPart, incomingImagePart] : [...partsWithoutRawText, incomingImagePart];

  if (incomingMessage.message?.messageId) {
    updatedMessage.messageId = incomingMessage.message.messageId;
  }

  if (incomingMessage.message && 'usage' in incomingMessage.message && 'usage' in updatedMessage) {
    updatedMessage.usage = incomingMessage.message.usage;
  }

  if (incomingMessage.message && 'enhancedPrompt' in incomingMessage.message) {
    updatedMessage.enhancedPrompt = incomingMessage.message.enhancedPrompt;
  }

  if (incomingMessage.message && 'mode' in incomingMessage.message) {
    updatedMessage.mode = incomingMessage.message.mode;
  }

  if (incomingMessage.message && 'context' in incomingMessage.message) {
    updatedMessage.context = incomingMessage.message.context;
  }

  return updatedMessage;
};

export const getLastThinkingMessage = (messages: MessagesRecord): ChatMessage | null => {
  const messageEntries = Object.entries(messages);

  if (messageEntries.length === 0) {
    return null;
  }

  const [_, lastMessage] = messageEntries[messageEntries.length - 1];

  if (lastMessage.role !== MessageRole.ASSISTANT) {
    return null;
  }

  if (!('isThinking' in lastMessage) || !lastMessage.isThinking) {
    return null;
  }

  return lastMessage;
};

const updateAssistantMessage = (messages: MessagesRecord, updateFn: (existing: ModelMessage) => ModelMessage): MessagesRecord | null => {
  const messageEntries = Object.entries(messages);

  if (messageEntries.length === 0) {
    return null;
  }

  const [lastMessageKey, lastMessage]: [string, ChatMessage] = messageEntries[messageEntries.length - 1];

  if (lastMessage.role === MessageRole.ASSISTANT) {
    const isThinking = 'isThinking' in lastMessage && (lastMessage as {isThinking: boolean}).isThinking;

    if (!isThinking) {
      const updatedMessage = updateFn(lastMessage);

      if (updatedMessage.messageId && updatedMessage.messageId !== lastMessageKey) {
        const newMessages = omitKey(messages, lastMessageKey);
        newMessages[updatedMessage.messageId] = updatedMessage;
        return newMessages;
      }

      return {
        ...messages,
        [lastMessageKey]: updatedMessage,
      };
    }
  }

  return null;
};

const appendMessage = (messages: MessagesRecord, newMessage: ChatMessage): MessagesRecord => {
  const key = newMessage.messageId;
  if (!key) {
    throw new Error('New message must have a message_id to append.');
  }

  return {
    ...messages,
    [key]: newMessage,
  };
};

export const replaceThinkingOrUpdateMessage = (messages: MessagesRecord, newMessage: ModelMessage, updateFn: (existing: ModelMessage) => ModelMessage): MessagesRecord => {
  const thinkingMessage = getLastThinkingMessage(messages);

  if (thinkingMessage) {
    return replaceThinkingMessage(messages, newMessage, thinkingMessage);
  }

  const allUpdatedMessages = updateAssistantMessage(messages, updateFn);
  if (allUpdatedMessages !== null) {
    return allUpdatedMessages;
  }

  return appendMessage(messages, newMessage);
};

export const isComponentPart = (part: ChatMessagePart, componentType: StreamComponentType): boolean =>
  part.type === ChatMessagePartType.COMPONENT && part.componentType === componentType;

/**
 * Return a deep copy of a model message with any prior skeleton placeholder part
 * (matched by `isExistingSkeleton`) stripped and `skeletonPart` appended. Shared by the
 * IMAGE_STREAMING_START and ENTITY_SKELETON_START handlers so the strip-then-append is
 * defined once; each caller supplies its own skeleton part and matcher.
 */
export const appendSkeletonToMessage = (
  message: ChatMessage,
  skeletonPart: ChatMessagePart,
  isExistingSkeleton: (part: ChatMessagePart) => boolean
): ModelMessage => {
  const updatedMessage = deepCopyMessage(message) as ModelMessage;
  const existingParts = updatedMessage.parts ?? [];
  updatedMessage.parts = [...existingParts.filter((part) => !isExistingSkeleton(part)), skeletonPart];
  return updatedMessage;
};

export const replaceLoadingText = (messages: MessagesRecord, newMessage: ModelMessage): MessagesRecord => {
  const thinkingMessage = getLastThinkingMessage(messages);

  if (!thinkingMessage) {
    return appendMessage(messages, newMessage);
  }

  const held = (thinkingMessage.parts ?? []).filter((part) => part.type !== ChatMessagePartType.TEXT);

  if (held.length === 0) {
    return replaceThinkingMessage(messages, newMessage, thinkingMessage);
  }

  const merged: ModelMessage = {...newMessage, parts: [...(newMessage.parts ?? []), ...held]};

  return replaceThinkingMessage(messages, merged, thinkingMessage);
};

/**
 * Replaces the last "thinking" message with the new message, OR
 * appends the new message.
 */
export const replaceThinkingOrAppendMessage = (messages: MessagesRecord, newMessage: ChatMessage): MessagesRecord => {
  const thinkingMessage = getLastThinkingMessage(messages);

  if (thinkingMessage) {
    return replaceThinkingMessage(messages, newMessage, thinkingMessage);
  }

  return appendMessage(messages, newMessage);
};

const replaceThinkingMessage = (messages: MessagesRecord, newMessage: ChatMessage, thinkingMessage: ChatMessage): MessagesRecord => {
  if (!thinkingMessage.messageId) {
    throw new Error('Thinking message must have a message_id to be replaced.');
  }

  if (!newMessage.messageId) {
    throw new Error('New message must have a message_id to replace an existing message.');
  }

  const msgs = omitKey(messages, thinkingMessage.messageId);
  msgs[newMessage.messageId] = newMessage;
  return msgs;
};

export const getChatTypeForMode = (mode: AiAgentMode): ChatType => {
  if (mode === AiAgentMode.AGENT) {
    return ChatType.AGENT;
  }

  return ChatType.DESIGN;
};
